// src/proformas/index.js — екран „Проформи" (Фаза 3.2). Пренесен дословно.
// Печатът и имейлът минават през window.printInvoice/mailDocOverlay (легаси
// до миграцията на фактурите). Кешът _proformas живее в app.js (мост).

import { el, clear, $, field, input, row, clickableRow } from '../shared/dom.js';
import { invoiceForm } from '../invoices/form.js';
import { dateInput, localTodayIso, guardDates } from '../shared/dates.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import totalsService from '../../services/totals.js';
import { emptyState, searchInput, docNo, dominantStatus, sentStatusCell, matchesStatus, RECEIVED_OPTION } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { auditBlock } from '../shared/audit.js';
import { contactPicker, bulkManager } from '../shared/pickers.js';
import { state, cq, activeCompanyObj, vatRateSelect, contacts as contactsCache, proformasCache, setProformas, invoicesCache, setInvoices, isAdminUser, canEditPerm } from '../shared/state.js';
import { unitDatalist, paymentSelect, lastPriceFor, lastPriceText, listPriceFor, listPriceText, repriceAuto } from '../shared/docLines.js';
import { stockTypes } from '../../services/stock.js';
import { suggestedBasis } from '../shared/vatBases.js';
import { sortTh, sortLabel } from '../shared/sortHeader.js';
import { regHead, regFilters, regField, regCount } from '../shared/register.js';

const PF_STATUS = { draft: ['Чернова', 'gray'], sent: ['Изпратена', 'blue'], invoiced: ['Фактурирана', 'green'] };

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  let proformas = [], contacts = [];
  // Контактите са помощни: отказът им (напр. без право) не бива да изпразва
  // самия списък — грешка от картата, 16.10.2026 (тогава Promise.all падаше
  // целият и екранът казваше „Още няма …").
  const [pfR, conR] = await Promise.allSettled([api('/proformas' + cq()), api('/contacts' + cq())]);
  if (pfR.status === 'fulfilled') proformas = pfR.value || []; else toast(pfR.reason.message, true);
  if (conR.status === 'fulfilled') contacts = conR.value || [];
  setProformas(proformas);
  const cName = (id) => { const c = contacts.find((x) => x.id === id); return c ? c.name : '—'; };
  const panel = el('div', { class: 'panel' });
  const bulk = bulkManager({ endpoint: '/proformas', refresh: () => window.CRM.renderView($('#page')), label: 'проформи', statuses: PF_STATUS });
  // Видът е като в „Доставки" (Макс, 29.10.2026): заглавието вляво, бутоните
  // вдясно на един ред, основният — последен. Проформите нямат второстепенни
  // бутони, затова редът носи само „+ Нова проформа" и лентата за избраните.
  // „Само преглед" (ОТЧЕТ-ПРОМТ-38, Д3): проформите са под правото
  // „Фактури" — без право за промяна „+ Нова проформа" го няма.
  const canEdit = canEditPerm('invoices');
  const head = regHead('Проформи', [],
    canEdit ? el('button', { class: 'btn sm', type: 'button', text: '+ Нова проформа', onClick: () => proformaForm(null, contacts) }) : null,
    bulk.toolbar);
  panel.appendChild(head);
  if (!proformas.length) { panel.appendChild(canEdit ? emptyState('◈', 'Още няма проформи.', '+ Нова проформа', () => proformaForm(null, contacts)) : emptyState('◈', 'Още няма проформи.')); page.appendChild(panel); return; }
  const flt = { q: '', status: '', year: '', sortKey: 'number', sortDir: 'desc' };
  const years = [...new Set(proformas.map((i) => (i.issue_date || '').slice(0, 4)).filter(Boolean))].sort().reverse();
  const search = searchInput({ placeholder: 'Търси № или клиент…', ariaLabel: 'Търсене на проформа по номер или клиент', onSearch: (q) => { flt.q = q; draw(); } });
  const statusSel = el('select', { onChange: (e) => { flt.status = e.target.value; draw(); } }, el('option', { value: '', text: 'Всички статуси' }), ...Object.keys(PF_STATUS).map((k) => el('option', { value: k, text: PF_STATUS[k][0] })),
    el('option', { value: RECEIVED_OPTION[0], text: RECEIVED_OPTION[1] }));
  const yearSel = el('select', { onChange: (e) => { flt.year = e.target.value; draw(); } }, el('option', { value: '', text: 'Всички години' }), ...years.map((y) => el('option', { value: y, text: y })));
  // Филтрите с надписи отгоре, като в „Доставки"; броячът — над таблицата,
  // за да се вижда до самия списък, а не в края на лентата.
  panel.appendChild(regFilters(
    regField('№ или клиент', search, 'l'),
    regField('Статус', statusSel),
    regField('Година', yearSel)));
  const countLbl = regCount();
  panel.appendChild(countLbl);
  const numOf = (i) => parseInt(String(i.number || '').replace(/\D/g, ''), 10) || 0;
  function setSort(k) { if (flt.sortKey === k) flt.sortDir = flt.sortDir === 'asc' ? 'desc' : 'asc'; else { flt.sortKey = k; flt.sortDir = 'asc'; } draw(); }
  const thNo = sortTh({}, () => setSort('number'));
  const thCl = sortTh({}, () => setSort('client'));
  const thDt = sortTh({ class: 'hide-sm' }, () => setSort('date'));
  const thSm = sortTh({ class: 'num' }, () => setSort('total'));
  const thSt = sortTh({}, () => setSort('status'));
  // Приглушаване на преобладаващия статус — както при фактурите, за да не
  // крещи цветен етикет на всеки ред, когато почти всички са с един статус.
  const _stDominant = dominantStatus(proformas);
  const tb = el('tbody');
  function draw() {
    bulk.reset(); clear(tb);
    let rows = proformas.filter((i) => {
      if (flt.status && !matchesStatus(i, flt.status)) return false;
      if (flt.year && (i.issue_date || '').slice(0, 4) !== flt.year) return false;
      if (flt.q) { const hay = (i.number + ' ' + cName(i.contact_id) + ' ' + (i.notes || '')).toLowerCase(); if (!hay.includes(flt.q)) return false; }
      return true;
    });
    const dir = flt.sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (flt.sortKey === 'client') { const x = cName(a.contact_id).toLowerCase(), y = cName(b.contact_id).toLowerCase(); return x < y ? -dir : x > y ? dir : 0; }
      if (flt.sortKey === 'date') { const x = a.issue_date || '', y = b.issue_date || ''; return x < y ? -dir : x > y ? dir : 0; }
      if (flt.sortKey === 'total') return ((Number(a.total) || 0) - (Number(b.total) || 0)) * dir;
      if (flt.sortKey === 'status') { const x = a.status || '', y = b.status || ''; return x < y ? -dir : x > y ? dir : 0; }
      return (numOf(a) - numOf(b)) * dir;
    });
    rows.forEach((pf) => {
      // Катинар за превърнатите във фактура — с подсказка защо.
      const lockMark = pf.locked_at
        ? el('span', { text: ' 🔒', title: 'Заключена при издаване на фактура на ' + dfmt(pf.locked_at), 'aria-label': 'Заключена при издаване на фактура на ' + dfmt(pf.locked_at) })
        : null;
      tb.appendChild(clickableRow(() => proformaForm(pf, contacts), 'Отвори проформа № ' + pf.number + (pf.locked_at ? ' (заключена)' : ''),
        bulk.cell(pf.id, 'проформа № ' + pf.number),
        el('td', { style: 'font-weight:600' }, '№ ', docNo(pf.number), lockMark),
        el('td', {}, cName(pf.contact_id)),
        el('td', { class: 'hide-sm' }, dfmt(pf.issue_date)),
        el('td', { class: 'num' }, money(pf.total, pf.currency)),
        sentStatusCell(pf, _stDominant, PF_STATUS)
      ));
    });
    sortLabel(thNo, '№', flt.sortKey === 'number' ? flt.sortDir : ''); sortLabel(thCl, 'Клиент', flt.sortKey === 'client' ? flt.sortDir : ''); sortLabel(thDt, 'Дата', flt.sortKey === 'date' ? flt.sortDir : ''); sortLabel(thSm, 'Сума', flt.sortKey === 'total' ? flt.sortDir : ''); sortLabel(thSt, 'Статус', flt.sortKey === 'status' ? flt.sortDir : '');
    countLbl.set(rows.length, proformas.length, flt.q);
  }
  // На телефон редът е карта — като при фактурите (ПРОМТ-37); reg-list дава
  // по-ниските редове на „Доставки".
  panel.appendChild(el('table', { class: 'cards-sm inv-cards reg-list' }, el('thead', {}, el('tr', {}, bulk.headTh, thNo, thCl, thDt, thSm, thSt)), tb));
  draw();
  page.appendChild(panel);
}

// Формата на проформата е ФОРМАТА НА ФАКТУРАТА (Макс, 20.10.2026: „нека
// проформата изглежда и работи както фактурата") — src/invoices/form.js с
// kind: 'proforma'. Тук остава само това, което е само на проформата:
// статусите ѝ и „Превърни във фактура".
function proformaForm(existing, contacts) {
  if (!contacts) contacts = contactsCache();
  return invoiceForm(existing, contacts, { kind: 'proforma', statuses: PF_STATUS, extraFoot: convertButton });
}


function convertButton(existing, contacts) {
  if (existing.locked_at) return null;
  return el('button', { class: 'btn sm', text: 'Превърни във фактура', onClick: async () => {
    if (!(await confirmDialog('Да създам ли фактура от тази проформа?', { okText: 'Създай' }))) return;
    // Проформа с 0 % ДДС не се превръщаше (грешка от картата): фактурата
    // иска основание за неначисляване, а то не се прехвърляше. Сега се
    // пренасят режимът и основанието; ако проформата няма основание —
    // нерегистрираната фирма получава чл. 113, ал. 9, а иначе — каквото
    // предлага режимът. Ако и така няма, отваряме фактурата попълнена,
    // за да го избере човекът.
    const zero = (Number(existing.vat_rate) || 0) === 0;
    let basis = String(existing.vat_basis || '').trim();
    if (zero && !basis) {
      const reg = !!String(((activeCompanyObj() || {}).vat_number) || '').trim();
      basis = suggestedBasis(reg ? (existing.tax_treatment || 'domestic') : 'not_registered') || '';
    }
    const invData = { company_id: existing.company_id, contact_id: existing.contact_id, issue_date: localTodayIso(), due_date: existing.due_date, currency: existing.currency, vat_rate: existing.vat_rate, payment_method: existing.payment_method, notes: existing.notes, items: existing.items, status: 'draft', tax_treatment: existing.tax_treatment || 'domestic', vat_basis: basis };
    if (zero && !basis) {
      closeModal();
      window.invoiceForm(Object.assign({}, invData, { issue_date: undefined }), contacts);
      toast('Изберете основание за 0 % ДДС и запазете фактурата. После отбележете проформата като „Фактурирана".', true);
      return;
    }
    try {
      const inv = await api('/invoices', { method: 'POST', body: JSON.stringify(invData) });
      await api('/proformas/' + existing.id, { method: 'PUT', body: JSON.stringify(Object.assign({}, existing, { status: 'invoiced', converted_invoice_id: inv.id })) });
      toast('Създадена фактура № ' + inv.number); closeModal(); window.CRM.renderView($('#page'));
    } catch (e) { toast(e.message, true); }
  } });
}
